import { React, useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { motion } from 'framer-motion';
import Table from 'react-bootstrap/Table';
import "./index.scss";

import ProfileAvatar from '../../../ui/ProfileAvatar';
import Pagination from '../../../ui/Pagination';
import Button from "../../../ui/Button";
import RadioButton from "../../../ui/RadioButton";
import SideModal from '../../../ui/SideModal';
import DropDown from "../../../ui/DropDown/index.js";
import SkeletonLoader from "../../../ui/SkeletonLoader/index.js";
import NoDataFound from "../../../ui/NoDataFound/index.js";

import ModifyDomain from "../ModifyDomain";
import AddDomain from "../../../../components/PostLogin/OrganizationDetails/AddDomain";

import { editIcon, filterIcon, filterCloseIcon, searchIcon } from "../../../../utils/icons.js";
import { formatDate, getUserImageUrl } from "../../../../utils/util.js";

import { useSelector, useDispatch } from 'react-redux';
import { getUsersListByOrganization, resetAddDomainResponse, resetUsersList, resetAssignUnassignResponse } from '../../../../store/actions/organizationActions.js';



const sortByOption = [
    {
        label: "First Name",
        value: "firstname"
    },
    {
        label: "Last Name",
        value: "lastname"
    },
    {
        label: "Email",
        value: "email"
    },
    {
        label: "Username",
        value: "username"
    },
    {
        label: "Last Login",
        value: "last_login"
    },
    {
        label: "Created At",
        value: "created_at"
    },
    {
        label: "Updated At",
        value: "updated_at"
    }
]

const filterRadioOption = [    
    {
        label: "Ascending",
        value: "asc"
    },
    {
        label: "Descending",
        value: "desc"
    }
]

const statusOption = [    
    {
        label: "Active",
        value: "active"
    },
    {
        label: "Inactive",
        value: "inactive"
    },
    {
        label: "Disabled",
        value: "disabled"
    }
]

const roleOption = [    
    {
        label: "Administrator",
        value: "administrator"
    },
    {
        label: "User",
        value: "user"
    },
    {
        label: "Read only",
        value: "read_only"
    }
]


const UsersTab = ({organizationDetail = "", customerDetails="", currentPageType="organizationDetails"}) => {

    const userListResponse = useSelector((state) => state.organization?.userListByOrganization);
    const addDomainResponse = useSelector((state) => state.organization?.addDomainResponse);
    const assignUnAssignResponse = useSelector((state) => state.organization?.assignUnAssignResponse)

    const dispatch = useDispatch();

    const { t } = useTranslation();

    const filterWrapperRef = useRef(null);

    const [userSearchValue, setUserSearchValue] = useState("");
    const [searchedValue, setSearchedValue] = useState("");
    const [isUserSearchFocused, setIsUserSearchFocused] = useState(false);
    //filter value
    const [selectedStatus, setSelectedStatus] = useState("");
    const [selectedRole, setSelectedRole] = useState("");
    const [selectedSortby, setSelectedSortby] = useState("");
    const [selectedSortOrder, setSelectedSortOrder] = useState("");
    const [showFilter, setShowFilter] = useState(false);

    //selected filter value
    const [filterSelectedStatus, setFilterSelectedStatus] = useState("");
    const [filterSelectedRole, setFilterSelectedRole] = useState("");
    const [filterSelectedSortby, setFilterSelectedSortby] = useState("");
    const [filterSelectedSortOrder, setFilterSelectedSortOrder] = useState("");

    const [showAdddomainSidePanel, setShowAdddomainSidePanel] = useState(false);
    const [userListState, setUsersListState] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(10);
    const [isLoading, setIsLoading] = useState(false);
    const [showModifySidePanel, setShowModifySidePanel] = useState(false);

    const applyFilter = () => {
        setCurrentPage(1);
        setFilterSelectedStatus(selectedStatus)
        setFilterSelectedRole(selectedRole)
        setFilterSelectedSortby(selectedSortby)
        setFilterSelectedSortOrder(selectedSortOrder)
        setShowFilter(false)       
    }

    const removeFilter = (type) => {
        setCurrentPage(1);
        if(type === "status") {
            setSelectedStatus("")
            setFilterSelectedStatus("")           
        } else if(type === "role") {
            setSelectedRole("")
            setFilterSelectedRole("")
        } else if (type === "sortorder") {
            setSelectedSortOrder("");
            setFilterSelectedSortOrder("")
        } else if (type === "sortby") {
            setSelectedSortby("");
            setSelectedSortOrder("");
            setFilterSelectedSortby("")
            setFilterSelectedSortOrder("")
        }
    }

    const displaySortByName = (type) => {
        if (type === "status") {
            const matchedOption = statusOption.find((data) => data.value === selectedStatus)
            return matchedOption ? matchedOption.label : "";
        } else if(type === "role") {
            const matchedOption = roleOption.find((data) => data.value === selectedRole)
            return matchedOption ? matchedOption.label : "";
        } else if (type === "sortOrder") {
            const matchedOption = filterRadioOption.find((data) => data.value === selectedSortOrder)
            return matchedOption ? matchedOption.label : "";
        } else {
            const matchedOption = sortByOption.find((data) => data.value === selectedSortby)
            return matchedOption ? matchedOption.label : "";
        }
    }

    const sortOrderChange = (data) => {
        setSelectedSortOrder(data)
    }

    const filterClicked = () => {
        setShowFilter(true)
    }

    const closeAddDomainSidePanel = () => {
        setShowAdddomainSidePanel(false)
    }

    const closeModifyModal = () => {
        closeModifySidePanel()
        dispatch(resetAssignUnassignResponse())
        if(assignUnAssignResponse?.detail?.status === "success") {
            dispatch(resetUsersList())
            fetchUser()
        }
    }

    const closeAddDomain = () => {
        closeAddDomainSidePanel()
        if(addDomainResponse != null) {
            dispatch(resetAddDomainResponse())
        }
    }

    const setCurrentPageandRecordSize = (currentPage, pageSize) => {
        setCurrentPage(currentPage);
        if(pageSize !== currentPageSize) {
            setCurrentPageSize(pageSize);
            setCurrentPage(1);
        }
    }

    const handleSearchKeyDown = (event) => {
        if (event.key === "Enter" && isUserSearchFocused) {
            searchUser()
        }
    }

    const searchUser = () => {
        setCurrentPage(1);
        setSearchedValue(userSearchValue);       
    }

    const fetchUser = useCallback(()=>{
        if(currentPageType === "organizationDetails") {
            const baseUrl = `&page=${currentPage}&page_size=${currentPageSize}&organization_id=${organizationDetail?.organization_id}`;
            const queryParams = searchedValue ? `&query=${searchedValue}` : "";
            const sortParams = (filterSelectedStatus ? `&status=${filterSelectedStatus}` : '') + (filterSelectedRole ? `&role=${filterSelectedRole}` : "") + (filterSelectedSortby ? `&sort_by=${filterSelectedSortby}` : '') + (filterSelectedSortOrder ? `&sort_order=${filterSelectedSortOrder}` : '');
            const url = `${baseUrl}${queryParams}${sortParams}`;
            setIsLoading(true)
            dispatch(getUsersListByOrganization(url, "list"));
        } else {
            const baseUrl = `&page=${currentPage}&page_size=${currentPageSize}&customer_id=${customerDetails?.customer_id}`;
            const queryParams = searchedValue ? `&query=${searchedValue}` : "";
            const sortParams = (filterSelectedStatus ? `&status=${filterSelectedStatus}` : '') + (filterSelectedRole ? `&role=${filterSelectedRole}` : "") + (filterSelectedSortby ? `&sort_by=${filterSelectedSortby}` : '') + (filterSelectedSortOrder ? `&sort_order=${filterSelectedSortOrder}` : '');
            const url = `${baseUrl}${queryParams}${sortParams}`;
            setIsLoading(true)
            dispatch(getUsersListByOrganization(url, "list"));
        }
        
    },[currentPage, currentPageSize, dispatch, organizationDetail, customerDetails, currentPageType, searchedValue, filterSelectedStatus, filterSelectedRole, filterSelectedSortby, filterSelectedSortOrder])

    const closeModifySidePanel = () => {
        setShowModifySidePanel(false)
    }

    useEffect(()=>{
        fetchUser()
    },[fetchUser])

    useEffect(()=>{
        if(userListResponse !== null){
            setUsersListState(userListResponse);
            setIsLoading(false)
        }
    },[userListResponse])

    useEffect(()=>{
        document.addEventListener("mousedown", handleClickOutside);

        function handleClickOutside(event) {
            if (filterWrapperRef.current && !filterWrapperRef.current.contains(event.target)) {
                if(showFilter){
                    setShowFilter(false)
                }
            }
        
        }
    },[showFilter])

    return (
        <>
            <div className="search-filter-container my-3 d-flex justify-content-between align-items-center">
                <div className="search-container d-flex align-items-center gap-3">
                    <div className="position-relative" onKeyDown={handleSearchKeyDown}>
                        <input className="search-input rounded" 
                            placeholder="Search User" 
                            value={userSearchValue} 
                            onChange={(e)=>setUserSearchValue(e.target.value)}
                            onFocus={() => setIsUserSearchFocused(true)}
                            onBlur={() => setIsUserSearchFocused(false)}
                        />
                        <span className="position-absolute search-icon">{searchIcon()}</span>
                    </div>
                    <button className="search-btn" onClick={()=>{searchUser()}}>{t("organizationDetailsPage.searchBtn")}</button>
                    <div className="position-relative">
                        <span className="filter-icon" onClick={filterClicked}>{filterIcon()}</span>
                        {showFilter && <div className="filters-container position-absolute p-3 rounded" ref={filterWrapperRef}>
                            <h6>Status</h6>
                            <DropDown 
                                placeHolder={"Status"}
                                options={statusOption}
                                isMulti={false}
                                isSearchable={false}
                                onChange={(data)=>{setSelectedStatus(data.value)}}
                                defaultSelectedValue={selectedStatus}
                            />
                            <h6 className="mt-4">Role</h6>
                            <DropDown 
                                placeHolder={"Role"}
                                options={roleOption}
                                isMulti={false}
                                isSearchable={false}
                                onChange={(data)=>{setSelectedRole(data.value)}}
                                defaultSelectedValue={selectedRole}
                            />
                            <h6 className="mt-4">{t("organizationDetailsPage.usersTable.sortBy")}</h6>
                            <DropDown 
                                placeHolder={"Sort By"}
                                options={sortByOption}
                                isMulti={false}
                                isSearchable={false}
                                onChange={(data)=>{setSelectedSortby(data.value)}}
                                defaultSelectedValue={selectedSortby}
                            />
                            <h6 className="mt-4">Sort Order</h6>
                            <div className="filter-user-status d-flex mb-4 gap-4">
                                {
                                    filterRadioOption.map((data,index)=>{
                                        return (
                                            <RadioButton radioId={data.label + index} radioValue={data.value} radioLabel={data.label} onChange={sortOrderChange} key={index} isChecked={data.value === selectedSortOrder} />
                                        )
                                    })
                                }
                            </div>
                            <div className="filter-apply-btn mt-3">
                                <Button buttonText={"Apply"}  buttonType={"primary"} disableButton={false} buttonClicked={()=>{applyFilter()}} />
                            </div>
                        </div>}
                    </div>
                    {!showFilter && <p className="selected-filters">
                        {filterSelectedStatus !== "" && <span className="d-inline-block mx-2">{displaySortByName("status")} <span onClick={() => removeFilter("status")}>{filterCloseIcon()}</span></span>}
                        {filterSelectedRole !== "" && <span className="d-inline-block mx-2">{displaySortByName("role")} <span onClick={() => removeFilter("role")}>{filterCloseIcon()}</span></span>}
                        {filterSelectedSortby !== "" && <span className="d-inline-block mx-2">{displaySortByName("sortby")} <span onClick={() => removeFilter("sortby")}>{filterCloseIcon()}</span></span>}
                        {filterSelectedSortOrder !== "" && <span className="d-inline-block mx-2">{displaySortByName("sortOrder")} <span onClick={() => removeFilter("sortorder")}>{filterCloseIcon()}</span></span>}
                    </p>}
                </div>
                <div className="sort-edit-container">
                    <div className="container d-flex gap-3 align-items-center">                       
                        <button className="edit-btn" onClick={()=>{                                                   
                            setShowModifySidePanel(true)
                        }}>
                            <p className="d-inline-block mx-1">{editIcon()}</p>
                            {t("organizationDetailsPage.modifyBtn")}
                        </button>
                        {/* <button onClick={()=>{setShowAdddomainSidePanel(true)}}>
                            <img src={addIcon} alt="delete" className="d-inline-block mx-1" />
                            {t("organizationDetailsPage.domainBtn")}
                        </button> */}
                    </div>
                </div>
            </div>
            <div className="domain-table-container users-tab-table">
                
                {
                    isLoading ?
                        <SkeletonLoader type={"line"} count={5} currentPage={"userstab"} />
                    :
                        <Table responsive="lg">
                            <thead>
                                <tr>
                                    <th>{t("organizationDetailsPage.usersTable.userDetails")}</th>
                                    <th>{t("organizationDetailsPage.usersTable.joined")}</th>
                                    <th>{t("organizationDetailsPage.usersTable.role")}</th>
                                    <th>{t("organizationDetailsPage.usersTable.status")}</th>
                                    <th>{t("organizationDetailsPage.usersTable.LastLogin")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    userListState?.results.length > 0 && 
                                    userListState?.results.map((data,index)=>{
                                        return (
                                            <motion.tr
                                                key={index}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.4, delay: index * 0.1 }}
                                            >
                                                <td>
                                                    <ProfileAvatar type="profile" firstName={data.firstname} lastName={data.lastname} secondaryText={data.email} imageUrl={getUserImageUrl(data)} />
                                                </td>
                                                <td>
                                                    <p>{formatDate(data.created_at)}</p>
                                                </td>
                                                <td>
                                                    <p className="text-capitalize">{data.role}</p>
                                                </td>
                                                <td>
                                                    <label className={`user-status text-capitalize ${data.status === "active" ? "active" : "disabled"}`}>{data.status}</label>
                                                </td>
                                                <td>
                                                    <p>{formatDate(data.last_login)}</p>
                                                </td>
                                            </motion.tr>
                                        )
                                    })
                                }
                            </tbody>
                        </Table>
                }
                {
                    !isLoading && userListState?.results.length === 0 && 
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                        >
                            <NoDataFound noDataText={"No data Found"}/>
                        </motion.div>
                }
                
                {userListResponse != null && !isLoading && userListResponse.meta.total_results !== 0 && <Pagination
                    className="pagination-bar"
                    currentPage={currentPage}
                    pageSize={currentPageSize}
                    totalCount={userListResponse.meta.total_results}
                    onPageChange={setCurrentPageandRecordSize}
                />}
            </div>
            <SideModal showSidePanel={showAdddomainSidePanel} hideSidePanel={closeAddDomainSidePanel}>
                {showAdddomainSidePanel && <AddDomain closeAddDomain={closeAddDomain}/>}
            </SideModal>
            <SideModal showSidePanel={showModifySidePanel} hideSidePanel={closeModifyModal}>
                {showModifySidePanel && <ModifyDomain selectedTab={"users"} clsoeModifyModal={closeModifyModal} customerDetails={customerDetails} organizationDetail={organizationDetail} currentPageType={currentPageType}/>}
            </SideModal>
        </>
    )
}

export default UsersTab;